import { IntentLocale } from "@/locales/localeid";
import { ConversationBuilderState } from "@/modules/conversation-builder";
import { CLUValidations } from "@/util/constants";
import { Form, Input, List, Modal } from "ant-design-vue";
import { ActionItem, fmtMsg } from "commonui";
import { cloneDeep, uniq } from "lodash";
import { v4 } from "uuid";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import {
	EntityLengths,
	isUtterenceValueValid,
	NormalizeSpaces,
	UTTERANCE_PATTERN_EXPRESSION
} from "../../conversation-builder-util";
import { Entity } from "../../models/entity.model";
import { Intent, Utterance } from "../../models/intent.model";

@Component({
	components: {
		FormItem: Form.Item,
		Input,
		List,
		ListItem: List.Item,
		ActionItem
	}
})
export default class IntentUtterance extends Vue {
	@Prop() intent!: Intent;

	public IntentLocale = IntentLocale;

	private invalidUtterances: string[] = [];
	private editedUtterances: string[] = [];
	public addButtonDisabled = true;
	public maxUtterances = EntityLengths.intent.utterance.max;
	public utteranceInvalidMessage: string = "";
	public addUtteranceText: string = "";
	public editUtteranceText: string = "";
	private isRemoving: boolean = false;
	private conversationStore = getModule(ConversationBuilderState);

	$refs!: {
		addUtterence: Vue;
		utteranceInput: HTMLInputElement;
	};

	created() {
		const { message } = this.validateUtterances(this.intent.utterances);
		this.utteranceInvalidMessage = message;
	}

	@Watch("intent.id")
	@Watch("intent.utterances")
	onIntentChange() {
		const { message } = this.validateUtterances(this.intent.utterances);
		this.utteranceInvalidMessage = message;
	}

	public get isCommonIntent(): boolean {
		return !!this.intent.commonIntentId;
	}

	public get utterances(): Utterance[] {
		return this.conversationStore.intents.directory[this.intent.id].utterances || [];
	}

	public get currentIntent(): Intent {
		return this.conversationStore.intents.directory[this.intent.id];
	}

	public isBeingEdited(id: string): boolean {
		return !!this.editedUtterances.find((f) => f === id);
	}

	public enterEditMode(id: string) {
		this.editedUtterances = [id];
		const utterance = this.utterances.find((u) => u.id === id);

		if (utterance) {
			this.editUtteranceText = utterance.value;
		}

		setTimeout(() => {
			this.$refs.utteranceInput.focus();
		}, 100);
	}
	public exitEditMode(id: string) {
		this.editedUtterances = this.editedUtterances.filter((f) => f !== id);
	}
	public cancelEdit(id: string) {
		this.exitEditMode(id);
	}
	public deleteUtterence(id: string, value: string) {
		Modal.confirm({
			title: this.$format(IntentLocale.IntentUtteranceDeleteConfirmation, { value }),
			onOk: () => {
				const updatedUtterances = this.utterances
					.filter((f) => f.id !== id)
					.map((m, index) => {
						m.order = index + 1;
						return m;
					});
				this.updateUtterances(updatedUtterances);
				const { message } = this.validateUtterances(updatedUtterances);
				this.utteranceInvalidMessage = message;
			}
		});
	}
	private updateUtterances(utterances: Utterance[]) {
		this.conversationStore.updateIntent({
			id: this.intent.id,
			intent: { utterances }
		});
	}

	public isInvalid(id: string) {
		return this.invalidUtterances.find((f) => f === id);
	}

	public addUtterenceValueChange(e: Event & any) {
		if (e.target && e.target.value && e.target.value.length) {
			let value = e.target.value;
			this.addButtonDisabled = false;

			if (!this.isRemoving && value.slice(-1) === "{" && this.currentIntent.entityId) {
				const entityName = this.conversationStore.entities.directory[this.currentIntent.entityId]?.name?.trim();

				if (entityName) {
					value = `${value}${entityName}}`;
					this.addUtteranceText = value;
				}
			}
		} else {
			this.addButtonDisabled = true;
		}
		this.isRemoving = false;
	}

	/**
	 * Called when user adds utterances, in intent properties
	 * @function
	 */
	public addUtterence() {
		const input: any = this.$refs.addUtterence;

		if (input.stateValue.length) {
			const maxOrder = this.utterances.length;
			const utterances: Utterance[] = [
				...this.utterances,
				{
					id: v4(),
					key: 0,
					value: NormalizeSpaces(input.stateValue),
					order: maxOrder + 1,
					isPattern: this.getIsPatternValue(input.stateValue)
				}
			];
			this.updateUtterances(utterances);
			const { message } = this.validateUtterances(utterances);
			this.utteranceInvalidMessage = message;
			input.stateValue = "";
			this.addUtteranceText = "";
			this.addButtonDisabled = true;
		}
	}

	public editUtterence(e: Event & { target: { value: any } }, id: string) {
		let value: any = e.target.value;
		if (value && value.length) {
			if (value.slice(-1) === "{" && this.intent.entityId) {
				const entityName = this.conversationStore.entities.directory[this.intent.entityId]?.name?.trim();

				if (entityName) {
					value = `${value}${entityName}}`;
				}
			}
			this.invalidUtterances = this.invalidUtterances.filter((f) => f != id);
			let currentUtterances = cloneDeep(this.utterances);
			currentUtterances = currentUtterances.map((m, index) => {
				if (m.id === id) {
					m.value = NormalizeSpaces(value);
					m.isPattern = this.getIsPatternValue(value);
				}
				m.order = index + 1;
				return m;
			});
			this.updateUtterances(currentUtterances);
			const { message } = this.validateUtterances(currentUtterances);
			this.utteranceInvalidMessage = message;
		} else {
			this.invalidUtterances = [...this.invalidUtterances, id];
		}
	}

	onKeyDown(e: KeyboardEvent) {
		if (e.key === "Backspace" || e.key === "Delete" || (e.key === "x" && e.ctrlKey)) {
			this.isRemoving = true;
		}
	}

	public onEditUtteranceChange() {
		if (!this.isRemoving && this.editUtteranceText.slice(-1) === "{" && this.intent.entityId) {
			const entityName = this.conversationStore.entities.directory[this.intent.entityId]?.name?.trim();

			if (entityName) {
				this.editUtteranceText = `${this.editUtteranceText}${entityName}}`;
			}
		}
		this.isRemoving = false;
	}

	public editUtteranceSubmit(id: string) {
		if (this.editUtteranceText && this.editUtteranceText.length) {
			this.invalidUtterances = this.invalidUtterances.filter((f) => f != id);
			let currentUtterances = cloneDeep(this.utterances);
			currentUtterances = currentUtterances.map((m, index) => {
				if (m.id === id) {
					m.value = NormalizeSpaces(this.editUtteranceText);
					m.isPattern = this.getIsPatternValue(this.editUtteranceText);
				}
				m.order = index + 1;
				return m;
			});
			this.updateUtterances(currentUtterances);
			const { message } = this.validateUtterances(currentUtterances);
			this.utteranceInvalidMessage = message;
		} else {
			this.invalidUtterances = [...this.invalidUtterances, id];
		}

		this.exitEditMode(id);
	}

	validateUtterances(utterances: Utterance[]) {
		const sameIntentUtterance = utterances.map((u) => u.value.trim().toLowerCase());
		let isValid = sameIntentUtterance.length === uniq(sameIntentUtterance).length;
		const validationResult = { message: "", isValid };

		let isLengthValid = true;

		sameIntentUtterance
			.filter((utr) => utr.includes("{" && "}"))
			.flatMap((ele) =>
				ele.length > CLUValidations.patternedUtterancesLengthLimit
					? (isLengthValid = isLengthValid && false)
					: (isLengthValid = isLengthValid && true)
			);

		if (!isLengthValid) {
			validationResult.isValid = isLengthValid;
			validationResult.message = fmtMsg(IntentLocale.PatternedUtterancesLengthInvalid);
			return validationResult;
		}

		if (!isValid) {
			validationResult.isValid = isValid;
			validationResult.message = fmtMsg(IntentLocale.IntentDuplicateUtterance);
			return validationResult;
		}

		isValid =
			!(sameIntentUtterance.length > EntityLengths.intent.utterance.max) &&
			!sameIntentUtterance.some(
				(u) =>
					u.length > EntityLengths.intent.utterance.maxLength ||
					u.length < EntityLengths.intent.utterance.minLength
			);

		if (!isValid) {
			validationResult.isValid = isValid;
			validationResult.message = fmtMsg(IntentLocale.IntentUtteranceLengthInvalid);
			return validationResult;
		}

		const otherUtterances = Object.keys(this.conversationStore.intents.directory)
			.filter((id) => id !== this.intent.id && this.conversationStore.intents.directory[id])
			.flatMap((intentId) =>
				this.conversationStore.intents.directory[intentId].utterances.map((u) => u.value.trim().toLowerCase())
			);

		isValid = !sameIntentUtterance.some((u) => otherUtterances.some((o) => o === u));

		if (!isValid) {
			validationResult.isValid = isValid;
			validationResult.message = fmtMsg(IntentLocale.IntentDuplicateUtterance);
			return validationResult;
		}

		isValid = !utterances.some((val) => !isUtterenceValueValid(val.value));

		if (!isValid) {
			validationResult.isValid = isValid;
			validationResult.message = fmtMsg(IntentLocale.IntentEntityUtterenceInvalid);
			return validationResult;
		}

		const usedEntity: Entity = this.intent.entityId
			? this.conversationStore.entities.directory[this.intent.entityId]
			: { id: "", key: 0, values: [], order: 0, name: "" };

		if (usedEntity && usedEntity.name) {
			const regex = new RegExp(UTTERANCE_PATTERN_EXPRESSION, "g");
			const entityName = `{${usedEntity.name}}`;

			isValid = !utterances.some((val) => {
				if (!this.isTagCountValid(val.value)) {
					return true;
				}

				const matches = val.value.match(regex);

				if (matches && matches.length && matches.some((m) => m !== entityName)) {
					return true;
				}

				return false;
			});

			if (!isValid) {
				validationResult.isValid = isValid;
				validationResult.message = fmtMsg(IntentLocale.IntentEntityInvalid);
				return validationResult;
			}
		}

		return validationResult;
	}

	public getIsPatternValue(utteranceValue: string): boolean {
		return new RegExp(UTTERANCE_PATTERN_EXPRESSION).test(utteranceValue);
	}

	isTagCountValid(text) {
		const openEntityRegex = /\{/g;
		const closeEntityRegex = /\}/g;
		let openCount = 0;
		let closeCount = 0;
		let openResult;
		let closeResult;

		while ((openResult = openEntityRegex.exec(text))) {
			openCount++;
		}

		while ((closeResult = closeEntityRegex.exec(text))) {
			closeCount++;
		}

		if ((openCount === 0 && closeCount === 0) || (openCount === 1 && closeCount === 1)) {
			return true;
		} else {
			false;
		}
	}
}
